import React, { createContext, useContext, useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { Amplify, Auth } from "aws-amplify";
import { USER_AUTH_CONFIG, DEV_AUTH_CONFIG } from "../aws-config";
import {
  developerEmailState,
  developerIdTokenState,
  linkOrgIdState,
  linkOrgUserIdState,
  originalPathState,
  platformState,
  userIdTokenState,
  userInfoState,
} from "../global-state";
import { getLinkedUser, getUserInfo } from "../setting/setting-service";
import { useHistory, useLocation } from "react-router-dom";
import useApiCall from "../common/api-call";
import ImportantToast from "../common/important-toast";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [originalPath, setOriginalPath] = useRecoilState(originalPathState);
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);
  const [developerIdToken, setDeveloperIdToken] = useRecoilState(
    developerIdTokenState
  );
  const [userIdToken, setUserIdToken] = useRecoilState(userIdTokenState);
  const [linkOrgId, setLinkOrgId] = useRecoilState(linkOrgIdState);
  const [linkOrgUserId, setLinkOrgUserId] = useRecoilState(linkOrgUserIdState);
  const [platform, setPlatform] = useRecoilState(platformState);
  const [developerEmail, setDeveloperEmail] =
    useRecoilState(developerEmailState);
  const [userAuthState, setUserAuthState] = useState(false);
  const [showAuthToast, setShowAuthToast] = useState(false);
  const [authToastMessage, setAuthToastMessage] = useState("");

  const { apiCall } = useApiCall();
  const location = useLocation();
  const history = useHistory();

  const showAuthError = (message) => {
    setAuthToastMessage(message);
    setShowAuthToast(true);
  };

  const checkUserFromApp = async () => {
    if (window.Android) {
      const token = window.Android.getIdToken();
      if (token) {
        setUserIdToken(token);
      }
      const resp = await getUserInfo(apiCall, userInfo, token);
      if (resp) {
        setUserInfo(resp);
        if (window.Android) {
          window.Android.updateUserInfo(JSON.stringify(resp));
        }
        setUserAuthState(true);
        setShowAuthToast(false);
      } else {
        setUserAuthState(false);
        showAuthError("Cannot reach the server");
      }
    }
  };

  const checkUserFromMLink = async () => {
    let idToken;
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      const session = await Auth.currentSession();
      idToken = session.getIdToken().getJwtToken();
      setUserIdToken(idToken);
    } catch (error) {
      setUserAuthState(false);
      handleAuthError(error);
      return;
    }
    const resp = await getLinkedUser(
      apiCall,
      linkOrgId,
      linkOrgUserId,
      idToken
    );
    if (resp) {
      if (resp.linked) {
        setUserInfo(resp.userInfo);
        setUserAuthState(true);
        setShowAuthToast(false);
      } else {
        setUserAuthState(false);
        history.push("/no-link");
      }
    } else {
      setUserAuthState(false);
      showAuthError("Cannot reach the server");
    }
  };

  const checkUserFromWeb = async () => {
    let idToken;
    try {
      Amplify.configure(USER_AUTH_CONFIG);
      const session = await Auth.currentSession();
      idToken = session.getIdToken().getJwtToken();
      setUserIdToken(idToken);
    } catch (error) {
      console.error(error);
      handleAuthError(error);
      return;
    }
    const resp = await getUserInfo(apiCall, userInfo, idToken);
    if (resp) {
      setUserInfo(resp);
      setUserAuthState(true);
      setShowAuthToast(false);
      if (resp.toWait || resp.inWait) {
        history.push("/waitlist");
        return;
      }
      if (resp.scopes.length === 0) {
        history.push("/init-scope");
        return;
      }
    } else {
      console.log("check user from web: error");
      setUserAuthState(false);
      showAuthError("Cannot reach the server");
    }
  };

  const checkUserFromDesktop = async () => {
    const idToken = await window.electron.ipcRenderer.invoke(
      "auth:get-id-token"
    );
    setUserIdToken(idToken);

    const resp = await getUserInfo(apiCall, userInfo, idToken);
    if (resp) {
      setUserInfo(resp);
      setUserAuthState(true);
      setShowAuthToast(false);
      if (resp.scopes.length === 0) {
        history.push("/init-scope");
        return;
      }
    } else {
      console.log("check user from desktop: error");
      setUserAuthState(false);
      showAuthError("Cannot reach the server");
    }
  };

  const checkUserAuthState = async () => {
    if (platform.source === "web") {
      await checkUserFromWeb();
    } else if (platform.source === "desktop") {
      await checkUserFromDesktop();
    } else {
      if (userIdToken) {
        await checkUserFromApp();
      } else {
        await checkUserFromMLink();
      }
    }
  };

  const handleAuthError = (error) => {
    console.log("Auth error:", error);
    if (typeof error === "string") {
      if (error.toLowerCase() === "no current user") {
        setUserAuthState(false);
        history.replace("/user-login");
        return;
      }
    }

    if (typeof error === "object" && error !== null) {
      const { name, code, message } = error;
      if (
        name === "NotAuthorizedException" ||
        code === "NotAuthorizedException" ||
        name === "TokenExpiredError" ||
        (message && message.includes("expired")) ||
        (message && message.includes("Invalid token"))
      ) {
        console.log("Token expired or invalid - redirecting to login");
        setUserAuthState(false);
        history.replace("/user-login");
        return;
      }
    }

    console.log("Non-token related error occurred");
    setUserAuthState(false);
    showAuthError("Authentication error");
  };

  const checkDeveloperAuthState = async () => {
    setOriginalPath(location.pathname + location.search);
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const email = session.getIdToken().payload["cognito:username"];
      setDeveloperIdToken(session.getIdToken().getJwtToken());
      setDeveloperEmail(email);
    } catch (error) {
      // developer login is optional
    }
  };

  const checkAuthState = async () => {
    if (location.pathname === "/user-login") {
      return;
    }
    setOriginalPath(location.pathname + location.search);
    try {
      await checkUserAuthState();
      await checkDeveloperAuthState();
    } catch (error) {
      console.error("Auth check error:", error);
      handleAuthError(error);
    }
  };

  const refreshUserToken = async () => {
    console.log("refresh user token");
    if (platform.source === "web") {
      try {
        Amplify.configure(USER_AUTH_CONFIG);
        const session = await Auth.currentSession();
        const idToken = session.getIdToken().getJwtToken();
        setUserIdToken(idToken);
        return idToken;
      } catch (error) {
        setUserAuthState(false);
        handleAuthError(error);
      }
    } else if (platform.source === "desktop") {
      const idToken = await window.electron.ipcRenderer.invoke(
        "auth:get-id-token"
      );
      setUserIdToken(idToken);
      return idToken;
    } else {
      if (window.Android) {
        const token = window.Android.getIdToken();
        if (token) {
          setUserIdToken(token);
          return token;
        }
      }
    }
  };

  const refreshDeveloperToken = async () => {
    try {
      Amplify.configure(DEV_AUTH_CONFIG);
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();
      setDeveloperIdToken(idToken);
      return idToken;
    } catch (error) {
      setUserAuthState(false);
      history.push("/developer-login");
    }
  };

  const handleReconnect = () => {
    setShowAuthToast(false);
    window.location.reload();
  };

  useEffect(() => {
    Amplify.configure(USER_AUTH_CONFIG);
    checkAuthState();

    const intervalId = setInterval(() => {
      refreshUserToken();
    }, 3 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  const value = {
    userAuthState,
    setUserAuthState,
    checkAuthState,
    refreshUserToken,
    refreshDeveloperToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ImportantToast
        isOpen={!showAuthToast}
        onAction={handleReconnect}
        message={authToastMessage}
        actionButtonText="Reconnect"
        excludedPaths={[
          "/user-login",
          "/user-login-callback",
          "/user-logout",
          "/developer-logout",
          "/developer-login",
          "/developer-login-callback",
          "/desktop-auth-wait",
          "/desktop-auth-done",
        ]}
        checkCondition={() => !showAuthToast}
        delay={0}
      />
    </AuthContext.Provider>
  );
};
